import { AfterViewInit, Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { PortalModuleMaterial } from '@src/app/model/portal-module-material';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { id } from '@ess/jg-rule-executor';
import { ConfirmationModalComponent } from '@src/app/components/modals/confirmation-modal/confirmation-modal.component';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { JsonModal } from '@src/app/components/utils/json-modal';
import { TypedFormModal } from '@src/app/components/utils/typed-form-modal';
import { TypedFormModalDataObject } from '@src/app/model/typed-form-modal-data-object';
import { MaterialInput, SubjectType } from '@src/app/generated/globalTypes';

@Component({
  selector: 'app-materials',
  templateUrl: './materials.component.html',
  styleUrls: ['./materials.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false,
})
export class MaterialsComponent implements AfterViewInit, OnInit, OnDestroy {
  private _componentDestroyed$: Subject<void> = new Subject();

  private _materialFailMsg: string = 'materials.upload.failure';
  private _skipFields = ['id'];

  public displayedColumnsMaterials: string[] = ['id', 'name', 'action'];
  public currentMaterials: MatTableDataSource<PortalModuleMaterial> = new MatTableDataSource<PortalModuleMaterial>([]);

  readonly sort = viewChild(MatSort);

  constructor(
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    private _apolloService: ApolloService
  ) {
  }

  public ngOnInit(): void {
    this._apolloService.getAllMaterials$()
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((materialsAngPageData) => {
        if (materialsAngPageData.materials) {
          this.currentMaterials.data = materialsAngPageData.materials;
        }
      });
  }

  public ngAfterViewInit(): void {
    this.currentMaterials.sort = this.sort();
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
  }

  public openAddMaterialDialog($event: any): void {
    this._openMaterialJsonDialog($event, mat => this._apolloService.newMaterial$(mat));
  }

  public deleteMaterial($event: any, material: PortalModuleMaterial): void {
    $event.stopPropagation();
    this._openConfirmationModal(
      'materials.deletion.header',
      'materials.deletion.content',
      material.name,
      this._apolloService.deleteMaterial$(material.id)
    );
  }

  public openEditMaterialJsonDialog($event: any, material: PortalModuleMaterial): void {
    this._openMaterialJsonDialog($event, mat => this._apolloService.updateMaterial$(material.id, mat), material);
  }

  public openEditMaterialFormDialog(material: PortalModuleMaterial): void {
    const dialogRef = TypedFormModal.openTypedFormModalDialog(
      this._dialog, new TypedFormModalDataObject(
        'material',
        SubjectType.MaterialsType,
        material,
        material.id,
        this._skipFields,
      )
    );
    TypedFormModal.useFormDialog(
      dialogRef,
      (
        (edited: MaterialInput) =>
          this._apolloService.updateMaterial$(material.id, PortalModuleMaterial.toJsonInput(edited))
      ).bind(this),
      this._translateService.instant(this._materialFailMsg),
      this._apolloService.getAllMaterialsRefresh.bind(this._apolloService)
    );
  }

  private _openMaterialJsonDialog(
    $event: any,
    updater: (bp: JSON) => Observable<boolean>,
    material?: PortalModuleMaterial
  ): void {
    $event.stopPropagation();
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'material',
      material || new PortalModuleMaterial(id('ID'), 'NAME'),
      'materials.upload.header',
      'buttons.submit'
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      updater.bind(this),
      this._translateService.instant(this._materialFailMsg),
      this._apolloService.getAllMaterialsRefresh.bind(this._apolloService)
    );
  }

  private _openConfirmationModal(
    header: string,
    content: string,
    material: string,
    updater: Observable<boolean>): void {
    const dialogRef = this._dialog.open(ConfirmationModalComponent,
      {
        data: {
          headerText: header,
          contentText: content,
          values: { name: material },
        },
      }
    );
    ConfirmationModalComponent.useConfirmDialog(
      dialogRef,
      updater,
      this._translateService.instant(this._materialFailMsg),
      this._apolloService.getAllMaterialsRefresh.bind(this._apolloService)
    );
  }

  public sortChange(sortState: Sort): void {
  }
}
